import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useAuth } from "../../useAuth";
import Button from "../../ui/Button";
import DrawerA11yWrapper from "../../ui/DrawerA11yWrapper";
import Input from "../../ui/Input";
import PageState from "../../ui/PageState";
import Select from "../../ui/Select";
import Table from "../../ui/Table";
import StatusBadge from "../../ui/StatusBadge";
import { spacing } from "../../ui/tokens";
import { RoleEnum } from "../../constants/roles";
import { canAccessAdminUsersStrict } from "../../accessControl";
import DestructiveConfirm from "../../components/common/DestructiveConfirm";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import {
  addCommitteeMembers,
  confirmPasswordReset,
  createAdminUser,
  createCommittee,
  getAccessConfig,
  getAdminUserDetail,
  listAdminAuditLogs,
  listAdminUsers,
  listCommittees,
  removeCommitteeMember,
  resetUserPassword,
  updateAdminUserAccess,
  updateUserRole,
  updateUserStatus,
} from "../../api/admin.api";

const ROLE_OPTIONS = [
  { value: RoleEnum.ADMIN, label: "Admin" },
  { value: RoleEnum.MANAGER, label: "Manager" },
  { value: RoleEnum.USER, label: "User" },
  { value: RoleEnum.EXEC, label: "Exec" },
  { value: RoleEnum.BOARD, label: "Board" },
  { value: RoleEnum.ANALYST, label: "Analyst" },
  { value: RoleEnum.VIEWER, label: "Viewer" },
];

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
];

const FALLBACK_ACCESS_PROFILES = [
  { key: "ADMIN", label: "Admin" },
  { key: "MANAGER", label: "Manager" },
  { key: "CONTRIBUTOR", label: "Contributor" },
  { key: "VIEWER", label: "Viewer" },
  { key: "AUDITOR", label: "Auditor" },
  { key: "CUSTOM", label: "Custom" },
];

const FALLBACK_MODULE_ACCESS_LEVELS = [
  { key: "NONE", label: "No access" },
  { key: "VIEW", label: "View" },
  { key: "EDIT", label: "Edit" },
  { key: "MANAGE", label: "Manage" },
];

const FALLBACK_MODULES = [
  { key: "overview", label: "My Overview" },
  { key: "risks", label: "Risks" },
  { key: "controls", label: "Controls" },
  { key: "actions", label: "Actions" },
  { key: "tasks", label: "Tasks" },
  { key: "incidents", label: "Incidents" },
  { key: "policies", label: "Policies" },
  { key: "kpis", label: "KPIs" },
  { key: "audit", label: "Audit" },
  { key: "governance", label: "Board Packs" },
  { key: "admin_users", label: "Admin User Portal" },
  { key: "admin_modules", label: "Admin Module Portal" },
];

const TABS = [
  { key: "users", label: "Users" },
  { key: "committees", label: "Committees" },
  { key: "audit-logs", label: "Audit Logs" },
  { key: "settings", label: "Settings" },
];

const EMPTY_ACCESS_CONFIG = {
  modules: FALLBACK_MODULES,
  access_levels: FALLBACK_MODULE_ACCESS_LEVELS,
  profiles: FALLBACK_ACCESS_PROFILES,
  profile_defaults: {},
};

const emptyAuditSummary = {
  items: [],
  total: 0,
  limit: 0,
  offset: 0,
};

const EMPTY_CREATE_USER_FORM = {
  username: "",
  email: "",
  password: "",
  role: RoleEnum.USER,
  status: "ACTIVE",
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const normalizeIdentityValue = (value) => String(value || "").trim().toLowerCase();

const extractAuditLogCollection = (payload) => {
  if (Array.isArray(payload)) {
    return {
      items: payload,
      total: payload.length,
      limit: payload.length,
      offset: 0,
    };
  }

  if (!payload || typeof payload !== "object") {
    return emptyAuditSummary;
  }

  const candidateItems = [payload.items, payload.results, payload.data].find((value) =>
    Array.isArray(value)
  );
  const items = Array.isArray(candidateItems) ? candidateItems : [];
  const total = Number.isFinite(Number(payload.total)) ? Number(payload.total) : items.length;
  const limit = Number.isFinite(Number(payload.limit)) ? Number(payload.limit) : items.length;
  const offset = Number.isFinite(Number(payload.offset)) ? Number(payload.offset) : 0;

  return {
    items,
    total,
    limit,
    offset,
  };
};

const buildModuleAccessMap = (modules, source, fallback = "NONE") => {
  const entries = Array.isArray(modules) && modules.length > 0 ? modules : FALLBACK_MODULES;
  return entries.reduce((acc, moduleDef) => {
    acc[moduleDef.key] = source?.[moduleDef.key] || fallback;
    return acc;
  }, {});
};

const areModuleAccessMapsEqual = (modules, left, right) => {
  const entries = Array.isArray(modules) && modules.length > 0 ? modules : FALLBACK_MODULES;
  return entries.every((moduleDef) => (left?.[moduleDef.key] || "NONE") === (right?.[moduleDef.key] || "NONE"));
};

const getProfileDefaults = (config, modules, accessProfile, fallbackSource = {}) => {
  if (!accessProfile || accessProfile === "CUSTOM") {
    return buildModuleAccessMap(modules, fallbackSource, "NONE");
  }
  const profileDefaults = config?.profile_defaults?.[accessProfile] || {};
  return buildModuleAccessMap(modules, profileDefaults, "NONE");
};

const getProfileLabel = (profiles, value) =>
  profiles.find((profile) => profile.key === value)?.label ||
  value?.replace(/_/g, " ").replace(/\b\w/g, (match) => match.toUpperCase()) ||
  "-";

const summarizeUserAccess = (row, profiles) => {
  const profileLabel = getProfileLabel(profiles, row?.access_profile);
  return row?.has_module_overrides ? `${profileLabel} + overrides` : profileLabel;
};

export default function UsersAdmin() {
  const { user, refreshAccess, accessReady = true } = useAuth();
  const canManagePortal = canAccessAdminUsersStrict(user);
  const currentUsername = normalizeIdentityValue(user?.username);
  const [activeTab, setActiveTab] = useState("users");

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [userActionState, setUserActionState] = useState({});
  const [usersMessage, setUsersMessage] = useState("");
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createUserForm, setCreateUserForm] = useState(EMPTY_CREATE_USER_FORM);
  const [createUserSaving, setCreateUserSaving] = useState(false);

  const [accessConfig, setAccessConfig] = useState(EMPTY_ACCESS_CONFIG);
  const [accessConfigLoading, setAccessConfigLoading] = useState(false);
  const [accessConfigError, setAccessConfigError] = useState("");
  const [accessEditorOpen, setAccessEditorOpen] = useState(false);
  const [accessEditorLoading, setAccessEditorLoading] = useState(false);
  const [accessEditorSaving, setAccessEditorSaving] = useState(false);
  const [accessEditorError, setAccessEditorError] = useState("");
  const [accessEditorMessage, setAccessEditorMessage] = useState("");
  const [accessEditorDetail, setAccessEditorDetail] = useState(null);
  const [accessEditorTargetUser, setAccessEditorTargetUser] = useState(null);
  const [accessFormProfile, setAccessFormProfile] = useState("");
  const [accessFormModules, setAccessFormModules] = useState({});

  const [committees, setCommittees] = useState([]);
  const [committeesLoading, setCommitteesLoading] = useState(false);
  const [committeesError, setCommitteesError] = useState("");
  const [committeeMembers, setCommitteeMembers] = useState({});
  const [committeeSelections, setCommitteeSelections] = useState({});
  const [committeeForm, setCommitteeForm] = useState({ name: "", description: "" });
  const [committeeSaving, setCommitteeSaving] = useState(false);
  const [committeeMessage, setCommitteeMessage] = useState("");

  const [auditLogs, setAuditLogs] = useState([]);
  const [auditSummary, setAuditSummary] = useState(emptyAuditSummary);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");
  const [auditSupported, setAuditSupported] = useState(true);
  const [auditFilters, setAuditFilters] = useState({
    actorId: "",
    action: "",
    targetType: "",
  });
  const [confirmResetUserId, setConfirmResetUserId] = useState(null);
  const [confirmRoleChange, setConfirmRoleChange] = useState(null);
  const [confirmStatusChange, setConfirmStatusChange] = useState(null);
  const [confirmCommitteeRemove, setConfirmCommitteeRemove] = useState(null);

  const [settingsState, setSettingsState] = useState({
    passwordPolicy: "standard",
    sessionTimeout: 30,
    mfaRequired: false,
  });
  const [resetConfirmForm, setResetConfirmForm] = useState({ token: "", newPassword: "" });
  const [resetConfirmSaving, setResetConfirmSaving] = useState(false);
  const [resetConfirmMessage, setResetConfirmMessage] = useState("");
  const [resetConfirmError, setResetConfirmError] = useState("");

  const accessDrawerTitleId = useId();
  const accessDrawerCloseRef = useRef(null);

  const handleTabSelect = useCallback((nextTab) => {
    setActiveTab((prev) => (prev === nextTab ? prev : nextTab));
  }, []);

  const userMap = useMemo(() => {
    const mapping = new Map();
    users.forEach((item) => {
      mapping.set(item.id, item);
    });
    return mapping;
  }, [users]);

  const moduleDefinitions = useMemo(
    () => (Array.isArray(accessConfig?.modules) && accessConfig.modules.length > 0 ? accessConfig.modules : FALLBACK_MODULES),
    [accessConfig]
  );
  const accessLevelDefinitions = useMemo(
    () =>
      Array.isArray(accessConfig?.access_levels) && accessConfig.access_levels.length > 0
        ? accessConfig.access_levels
        : FALLBACK_MODULE_ACCESS_LEVELS,
    [accessConfig]
  );
  const accessProfileDefinitions = useMemo(
    () =>
      Array.isArray(accessConfig?.profiles) && accessConfig.profiles.length > 0
        ? accessConfig.profiles
        : FALLBACK_ACCESS_PROFILES,
    [accessConfig]
  );

  const selectedProfileDefaults = useMemo(
    () =>
      getProfileDefaults(
        accessConfig,
        moduleDefinitions,
        accessFormProfile,
        accessEditorDetail?.effective_access || accessEditorDetail?.stored_module_access || {}
      ),
    [accessConfig, moduleDefinitions, accessFormProfile, accessEditorDetail]
  );

  const accessHasOverrides = useMemo(() => {
    if (!accessEditorDetail) return false;
    if (accessFormProfile === "CUSTOM") return true;
    return !areModuleAccessMapsEqual(moduleDefinitions, accessFormModules, selectedProfileDefaults);
  }, [accessEditorDetail, accessFormModules, accessFormProfile, moduleDefinitions, selectedProfileDefaults]);

  const accessIsCurrentUser = useMemo(
    () => normalizeIdentityValue(accessEditorDetail?.username) === currentUsername,
    [accessEditorDetail, currentUsername]
  );

  const loadUsers = useCallback(async () => {
    if (!canManagePortal) return;
    setUsersLoading(true);
    setUsersError("");
    try {
      const res = await listAdminUsers();
      const payload = res?.data || [];
      setUsers(Array.isArray(payload) ? payload : []);
    } catch (err) {
      setUsers([]);
      setUsersError(err?.detail || err?.message || "Unable to load users.");
    } finally {
      setUsersLoading(false);
    }
  }, [canManagePortal]);

  const loadAccessConfig = useCallback(
    async ({ silent = false } = {}) => {
      if (!canManagePortal) return EMPTY_ACCESS_CONFIG;
      if (!silent) {
        setAccessConfigLoading(true);
      }
      setAccessConfigError("");
      try {
        const res = await getAccessConfig();
        const payload = res?.data || EMPTY_ACCESS_CONFIG;
        const nextConfig = {
          modules: Array.isArray(payload.modules) && payload.modules.length > 0 ? payload.modules : FALLBACK_MODULES,
          access_levels:
            Array.isArray(payload.access_levels) && payload.access_levels.length > 0
              ? payload.access_levels
              : FALLBACK_MODULE_ACCESS_LEVELS,
          profiles:
            Array.isArray(payload.profiles) && payload.profiles.length > 0
              ? payload.profiles
              : FALLBACK_ACCESS_PROFILES,
          profile_defaults: payload.profile_defaults || {},
        };
        setAccessConfig(nextConfig);
        return nextConfig;
      } catch (err) {
        const nextError = err?.detail || err?.message || "Unable to load access configuration.";
        setAccessConfigError(nextError);
        return accessConfig;
      } finally {
        if (!silent) {
          setAccessConfigLoading(false);
        }
      }
    },
    [accessConfig, canManagePortal]
  );

  const loadCommittees = useCallback(async () => {
    if (!canManagePortal) return;
    setCommitteesLoading(true);
    setCommitteesError("");
    try {
      const res = await listCommittees();
      const payload = res?.data || [];
      const list = Array.isArray(payload) ? payload : [];
      setCommittees(list);
      setCommitteeMembers((prev) => {
        const next = { ...prev };
        list.forEach((committee) => {
          const rawMembers =
            committee.member_ids ||
            committee.user_ids ||
            committee.members ||
            committee.users ||
            [];
          if (Array.isArray(rawMembers)) {
            next[committee.id] = rawMembers
              .map((member) => (typeof member === "object" ? member.id : member))
              .filter((id) => id != null);
          }
        });
        return next;
      });
    } catch (err) {
      setCommittees([]);
      setCommitteesError(err?.detail || err?.message || "Unable to load committees.");
    } finally {
      setCommitteesLoading(false);
    }
  }, [canManagePortal]);

  const loadAuditLogs = useCallback(async () => {
    if (!canManagePortal) return;
    setAuditLoading(true);
    setAuditError("");
    try {
      const params = {};
      if (auditFilters.actorId) {
        params.actor_id = auditFilters.actorId;
      }
      if (auditFilters.action) {
        params.action = auditFilters.action;
      }
      if (auditFilters.targetType) {
        params.target_type = auditFilters.targetType;
      }
      const res = await listAdminAuditLogs(params);
      const payload = res?.data ?? res;
      const nextCollection = extractAuditLogCollection(payload);
      setAuditLogs(nextCollection.items || []);
      setAuditSummary({
        total: nextCollection.total,
        limit: nextCollection.limit,
        offset: nextCollection.offset,
      });
      setAuditSupported(true);
    } catch (err) {
      const status = err?.status;
      setAuditLogs([]);
      setAuditSummary(emptyAuditSummary);
      if (status === 404) {
        setAuditSupported(false);
        setAuditError("Audit log endpoints are not available on this server.");
      } else if (status === 403) {
        setAuditSupported(false);
        setAuditError("Audit logs are restricted to administrators.");
      } else {
        setAuditSupported(true);
        setAuditError(err?.detail || err?.message || "Unable to load audit logs.");
      }
    } finally {
      setAuditLoading(false);
    }
  }, [auditFilters, canManagePortal]);

  useEffect(() => {
    loadUsers();
    loadCommittees();
    loadAuditLogs();
  }, [loadUsers, loadCommittees, loadAuditLogs]);

  const handleRoleChange = async (userId, nextRole) => {
    setUsersError("");
    setUsersMessage("");
    setUserActionState((prev) => ({ ...prev, [userId]: { ...(prev[userId] || {}), role: true } }));
    try {
      await updateUserRole(userId, nextRole);
      await loadUsers();
      setUsersMessage(`Role updated to ${nextRole}.`);
    } catch (err) {
      setUsersError(err?.detail || err?.message || "Unable to update role.");
    } finally {
      setUserActionState((prev) => ({ ...prev, [userId]: { ...(prev[userId] || {}), role: false } }));
    }
  };

  const handleStatusUpdate = async (userId, nextStatus) => {
    setUsersError("");
    setUsersMessage("");
    setUserActionState((prev) => ({ ...prev, [userId]: { ...(prev[userId] || {}), status: true } }));
    try {
      await updateUserStatus(userId, nextStatus);
      await loadUsers();
      setUsersMessage(`Status updated to ${nextStatus}.`);
    } catch (err) {
      setUsersError(err?.detail || err?.message || "Unable to update status.");
    } finally {
      setUserActionState((prev) => ({ ...prev, [userId]: { ...(prev[userId] || {}), status: false } }));
    }
  };

  const requestRoleChange = (targetUser, nextRole) => {
    if (!targetUser || !nextRole || nextRole === targetUser.role) return;
    setConfirmRoleChange({
      userId: targetUser.id,
      previousRole: targetUser.role,
      nextRole,
    });
  };

  const requestStatusToggle = (userId, currentStatus) => {
    const nextStatus = currentStatus === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    setConfirmStatusChange({ userId, nextStatus });
  };

  const handlePasswordReset = async (userId) => {
    setUserActionState((prev) => ({ ...prev, [userId]: { ...(prev[userId] || {}), reset: true } }));
    setUsersError("");
    setUsersMessage("");
    try {
      await resetUserPassword(userId);
      setUsersMessage("Password reset initiated. Reset token delivery is handled out-of-band.");
    } catch (err) {
      setUsersError(err?.detail || err?.message || "Unable to reset password.");
    } finally {
      setUserActionState((prev) => ({ ...prev, [userId]: { ...(prev[userId] || {}), reset: false } }));
    }
  };

  const closeCreateUserForm = useCallback(() => {
    setCreateUserOpen(false);
    setCreateUserForm(EMPTY_CREATE_USER_FORM);
  }, []);

  const handleCreateUser = async (event) => {
    event.preventDefault();
    const username = createUserForm.username.trim();
    const email = createUserForm.email.trim();
    const password = createUserForm.password;

    setUsersError("");
    setUsersMessage("");
    if (!username || !email || !password.trim()) {
      setUsersError("Username, email, and password are required.");
      return;
    }

    setCreateUserSaving(true);
    try {
      await createAdminUser({
        username,
        email,
        password,
        role: createUserForm.role || RoleEnum.USER,
        status: createUserForm.status || "ACTIVE",
      });
      closeCreateUserForm();
      await loadUsers();
      setUsersMessage(`Created user ${username}.`);
    } catch (err) {
      setUsersError(err?.detail || err?.message || "Unable to create user.");
    } finally {
      setCreateUserSaving(false);
    }
  };

  const handleResetConfirmSubmit = async (event) => {
    event.preventDefault();
    if (!resetConfirmForm.token.trim() || !resetConfirmForm.newPassword) {
      setResetConfirmError("Token and new password are required.");
      return;
    }
    setResetConfirmSaving(true);
    setResetConfirmMessage("");
    setResetConfirmError("");
    const token = resetConfirmForm.token.trim();
    const newPassword = resetConfirmForm.newPassword;
    setResetConfirmForm({ token: "", newPassword: "" });
    try {
      await confirmPasswordReset(token, newPassword);
      setResetConfirmMessage("Password reset confirmed.");
    } catch (err) {
      setResetConfirmError(err?.detail || err?.message || "Unable to confirm reset.");
    } finally {
      setResetConfirmSaving(false);
    }
  };

  const handleCommitteeCreate = async (event) => {
    event.preventDefault();
    setCommitteeMessage("");
    if (!committeeForm.name.trim()) {
      setCommitteeMessage("Committee name is required.");
      return;
    }
    setCommitteeSaving(true);
    try {
      await createCommittee({
        name: committeeForm.name.trim(),
        description: committeeForm.description.trim() || null,
      });
      setCommitteeForm({ name: "", description: "" });
      setCommitteeMessage("Committee created.");
      await loadCommittees();
    } catch (err) {
      setCommitteeMessage(err?.detail || err?.message || "Unable to create committee.");
    } finally {
      setCommitteeSaving(false);
    }
  };

  const handleMemberAdd = async (committeeId) => {
    const selectedId = committeeSelections[committeeId];
    if (!selectedId) return;
    try {
      await addCommitteeMembers(committeeId, [Number(selectedId)]);
      setCommitteeMembers((prev) => {
        const existing = new Set(prev[committeeId] || []);
        existing.add(Number(selectedId));
        return { ...prev, [committeeId]: Array.from(existing) };
      });
      setCommitteeSelections((prev) => ({ ...prev, [committeeId]: "" }));
    } catch (err) {
      setCommitteesError(err?.detail || err?.message || "Unable to add member.");
    }
  };

  const handleMemberRemove = async (committeeId, userId) => {
    try {
      await removeCommitteeMember(committeeId, userId);
      setCommitteeMembers((prev) => ({
        ...prev,
        [committeeId]: (prev[committeeId] || []).filter((id) => id !== userId),
      }));
    } catch (err) {
      setCommitteesError(err?.detail || err?.message || "Unable to remove member.");
    }
  };

  const requestMemberRemove = (committeeId, userId) => {
    const member = userMap.get(userId);
    const label = member?.username || member?.email || `User #${userId}`;
    setConfirmCommitteeRemove({ committeeId, userId, label });
  };

  const closeAccessEditor = useCallback(() => {
    setAccessEditorOpen(false);
    setAccessEditorLoading(false);
    setAccessEditorSaving(false);
    setAccessEditorError("");
    setAccessEditorMessage("");
    setAccessEditorDetail(null);
    setAccessEditorTargetUser(null);
    setAccessFormProfile("");
    setAccessFormModules({});
  }, []);

  const openAccessEditor = useCallback(
    async (targetUser) => {
      if (!targetUser?.id) return;
      setAccessEditorOpen(true);
      setAccessEditorLoading(true);
      setAccessEditorSaving(false);
      setAccessEditorError("");
      setAccessEditorMessage("");
      setAccessEditorTargetUser(targetUser);
      try {
        const configPromise =
          Object.keys(accessConfig?.profile_defaults || {}).length > 0
            ? Promise.resolve(accessConfig)
            : loadAccessConfig({ silent: true });
        const [resolvedConfig, detailRes] = await Promise.all([configPromise, getAdminUserDetail(targetUser.id)]);
        const detail = detailRes?.data || detailRes;
        if (!detail || !detail.id) {
          setAccessEditorDetail(null);
          setAccessEditorError("");
          return;
        }
        const nextConfig = resolvedConfig || accessConfig || EMPTY_ACCESS_CONFIG;
        const effectiveAccess =
          detail?.effective_access || detail?.stored_module_access || buildModuleAccessMap(nextConfig.modules, {}, "NONE");
        setAccessConfig(nextConfig);
        setAccessEditorDetail(detail);
        setAccessFormProfile(detail?.access_profile || "VIEWER");
        setAccessFormModules(buildModuleAccessMap(nextConfig.modules, effectiveAccess, "NONE"));
      } catch (err) {
        setAccessEditorDetail(null);
        setAccessEditorError(err?.detail || err?.message || "Unable to load user access.");
      } finally {
        setAccessEditorLoading(false);
      }
    },
    [accessConfig, loadAccessConfig]
  );

  const handleAccessProfileSelect = useCallback(
    (nextProfile) => {
      setAccessEditorError("");
      setAccessEditorMessage("");
      setAccessFormProfile(nextProfile);
      if (nextProfile === "CUSTOM") {
        return;
      }
      setAccessFormModules(
        getProfileDefaults(
          accessConfig,
          moduleDefinitions,
          nextProfile,
          accessEditorDetail?.effective_access || accessEditorDetail?.stored_module_access || {}
        )
      );
    },
    [accessConfig, accessEditorDetail, moduleDefinitions]
  );

  const handleAccessModuleChange = useCallback((moduleKey, accessLevel) => {
    setAccessEditorError("");
    setAccessEditorMessage("");
    setAccessFormModules((prev) => ({ ...prev, [moduleKey]: accessLevel }));
  }, []);

  const handleAccessSave = useCallback(async () => {
    if (!accessEditorDetail?.id) return;
    setAccessEditorSaving(true);
    setAccessEditorError("");
    setAccessEditorMessage("");
    try {
      const payload = {
        access_profile: accessFormProfile || accessEditorDetail.access_profile || "VIEWER",
        module_access: buildModuleAccessMap(moduleDefinitions, accessFormModules, "NONE"),
      };
      const res = await updateAdminUserAccess(accessEditorDetail.id, payload);
      const updatedDetail = res?.data || res;
      setAccessEditorDetail(updatedDetail);
      setAccessFormProfile(updatedDetail?.access_profile || payload.access_profile);
      setAccessFormModules(
        buildModuleAccessMap(
          moduleDefinitions,
          updatedDetail?.effective_access || updatedDetail?.stored_module_access || payload.module_access,
          "NONE"
        )
      );
      setAccessEditorMessage("User access updated.");
      await loadUsers();
      if (Number(updatedDetail?.id) === Number(user?.id) && typeof refreshAccess === "function") {
        refreshAccess();
      }
    } catch (err) {
      setAccessEditorError(err?.detail || err?.message || "Unable to update user access.");
    } finally {
      setAccessEditorSaving(false);
    }
  }, [accessEditorDetail, accessFormModules, accessFormProfile, loadUsers, moduleDefinitions, refreshAccess, user?.id]);

  const renderUsersTab = () => {
    const userColumns = [
      { id: "username", header: "Username", accessor: "username" },
      { id: "email", header: "Email", accessor: (row) => row.email || "-" },
      {
        id: "legacyRole",
        header: "Legacy Role (business rules)",
        accessor: (row) => {
          const isRoleSaving = userActionState[row.id]?.role;
          const isCurrentUser = normalizeIdentityValue(row.username) === currentUsername;
          return (
            <div style={{ display: "grid", gap: 4 }}>
              <Select
                ariaLabel={`Role for ${row.username || "user"}`}
                value={row.role || RoleEnum.USER}
                onChange={(e) => requestRoleChange(row, e.target.value)}
                options={ROLE_OPTIONS}
                disabled={isRoleSaving || isCurrentUser}
                style={{ minWidth: 140 }}
              />
              <span style={inlineHelperStyle}>Role changes do not change the access profile or module settings. Use Edit Access.</span>
              {isCurrentUser && (
                <span style={inlineHelperStyle}>
                  Current account. Ask another admin to change your role.
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "accessProfile",
        header: "Access Profile",
        accessor: (row) => summarizeUserAccess(row, accessProfileDefinitions),
      },
      {
        id: "moduleAccess",
        header: "Module Access",
        accessor: (row) => (
          <div style={{ display: "grid", gap: 4 }}>
            <span>{row?.module_access_summary?.summary_text || "No access"}</span>
            {row?.has_module_overrides && (
              <span style={inlineHelperStyle}>Custom module overrides are active.</span>
            )}
          </div>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessor: (row) => {
          const isStatusSaving = userActionState[row.id]?.status;
          const nextStatusLabel = row.status === "ACTIVE" ? "Suspend" : "Activate";
          const isCurrentUser = normalizeIdentityValue(row.username) === currentUsername;
          return (
            <div style={{ display: "grid", gap: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: spacing.sm }}>
                <StatusBadge
                  variant="status"
                  value={row.status}
                  label={STATUS_OPTIONS.find((opt) => opt.value === row.status)?.label || row.status}
                />
                <Button
                  variant="secondary"
                  onClick={() => requestStatusToggle(row.id, row.status)}
                  disabled={isStatusSaving || isCurrentUser}
                >
                  {isStatusSaving ? "Saving..." : nextStatusLabel}
                </Button>
              </div>
              {isCurrentUser && (
                <span style={inlineHelperStyle}>Current account. Self-suspension is blocked.</span>
              )}
            </div>
          );
        },
      },
      {
        id: "lastLogin",
        header: "Last Login",
        accessor: (row) => formatDateTime(row.last_login),
      },
      {
        id: "actions",
        header: "Actions",
        accessor: (row) => {
          const isResetting = userActionState[row.id]?.reset;
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
              <Button variant="secondary" onClick={() => openAccessEditor(row)}>
                Edit Access
              </Button>
              <Button
                variant="secondary"
                onClick={() => setConfirmResetUserId(row.id)}
                disabled={isResetting}
              >
                {isResetting ? "Resetting..." : "Reset Password"}
              </Button>
            </div>
          );
        },
      },
    ];

    return (
      <div style={{ display: "grid", gap: 16 }}>
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h2 style={{ marginTop: 0 }}>Users</h2>
              <p style={{ marginBottom: 0, color: "#475467" }}>
                Manage roles, statuses, and credentials for platform users.
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Button
                variant={createUserOpen ? "secondary" : "primary"}
                onClick={() => {
                  setUsersError("");
                  setUsersMessage("");
                  setCreateUserOpen((prev) => !prev);
                  if (createUserOpen) {
                    setCreateUserForm(EMPTY_CREATE_USER_FORM);
                  }
                }}
                disabled={createUserSaving}
              >
                {createUserOpen ? "Close Create User" : "Create User"}
              </Button>
              <Button variant="secondary" onClick={loadUsers} disabled={usersLoading}>
                {usersLoading ? "Refreshing..." : "Refresh"}
              </Button>
            </div>
          </div>
          {usersError && users.length > 0 && <div style={{ color: "#b91c1c", marginTop: 8 }}>{usersError}</div>}
          {usersMessage && <div style={{ color: "#166534", marginTop: 8 }}>{usersMessage}</div>}
          {accessConfigError && <div style={{ color: "#b91c1c", marginTop: 8 }}>{accessConfigError}</div>}
        </div>

        {createUserOpen && (
          <div style={cardStyle}>
            <h3 style={{ marginTop: 0 }}>Create User</h3>
            <form
              onSubmit={handleCreateUser}
              style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}
            >
              <Input
                label="Username"
                value={createUserForm.username}
                onChange={(event) =>
                  setCreateUserForm((prev) => ({ ...prev, username: event.target.value }))
                }
                autoComplete="off"
              />
              <Input
                label="Email"
                type="email"
                value={createUserForm.email}
                onChange={(event) =>
                  setCreateUserForm((prev) => ({ ...prev, email: event.target.value }))
                }
                autoComplete="off"
              />
              <Input
                label="Temporary Password"
                type="password"
                value={createUserForm.password}
                onChange={(event) =>
                  setCreateUserForm((prev) => ({ ...prev, password: event.target.value }))
                }
                autoComplete="new-password"
              />
              <Select
                label="Legacy Role"
                value={createUserForm.role}
                options={ROLE_OPTIONS}
                onChange={(event) =>
                  setCreateUserForm((prev) => ({ ...prev, role: event.target.value }))
                }
              />
              <Select
                label="Status"
                value={createUserForm.status}
                options={STATUS_OPTIONS}
                onChange={(event) =>
                  setCreateUserForm((prev) => ({ ...prev, status: event.target.value }))
                }
              />
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                <Button type="submit" disabled={createUserSaving}>
                  {createUserSaving ? "Creating..." : "Create User"}
                </Button>
                <Button type="button" variant="secondary" onClick={closeCreateUserForm} disabled={createUserSaving}>
                  Cancel
                </Button>
              </div>
            </form>
            <p style={{ marginBottom: 0, marginTop: 12, color: "#475467", fontSize: 12 }}>
              New users inherit default module access from their legacy role. Use Edit Access for custom module overrides.
            </p>
          </div>
        )}

        <div style={cardStyle}>
          {usersError && users.length === 0 ? (
            <PageState
              state="error"
              align="left"
              compact
              title="Users are unavailable"
              description={`${usersError} Refresh to try again.`}
              action={
                <Button type="button" variant="secondary" onClick={loadUsers}>
                  Refresh
                </Button>
              }
            />
          ) : usersLoading ? (
            <PageState
              state="loading"
              align="left"
              compact
              title="Loading users"
              description="Glyder is loading platform users, roles, and access status."
            />
          ) : users.length === 0 ? (
            <PageState
              state="empty"
              align="left"
              compact
              title="No users found"
              description="Users will appear here once accounts have been provisioned in this environment."
              action={
                <Button type="button" variant="secondary" onClick={loadUsers}>
                  Refresh
                </Button>
              }
            />
          ) : (
            <Table
              columns={userColumns}
              data={users}
              getRowKey={(row) => row.id || row.username}
              emptyState="No users found."
            />
          )}
        </div>
      </div>
    );
  };

  const renderCommitteesTab = () => (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Committees</h2>
        <p style={{ marginBottom: 0, color: "#475467" }}>
          Assign users to committees to scope board pack access and review.
        </p>
      </div>

      <div style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Create Committee</h3>
        <form onSubmit={handleCommitteeCreate} style={{ display: "grid", gap: 12 }}>
          <Input
            label="Name"
            value={committeeForm.name}
            onChange={(e) => setCommitteeForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Audit Committee"
          />
          <Input
            label="Description"
            value={committeeForm.description}
            onChange={(e) => setCommitteeForm((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Optional"
          />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Button type="submit" variant="primary" disabled={committeeSaving}>
              {committeeSaving ? "Creating..." : "Create Committee"}
            </Button>
            {committeeMessage && <span style={{ color: "#1f2937" }}>{committeeMessage}</span>}
          </div>
        </form>
      </div>

      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ marginTop: 0 }}>Committee Directory</h3>
          <Button variant="secondary" onClick={loadCommittees} disabled={committeesLoading}>
            {committeesLoading ? "Refreshing..." : "Refresh"}
          </Button>
        </div>
        {committeesError && committees.length > 0 && <div style={{ color: "#b91c1c" }}>{committeesError}</div>}
        {committeesError && committees.length === 0 ? (
          <PageState
            state="error"
            align="left"
            compact
            title="Committees are unavailable"
            description={`${committeesError} Refresh to try again.`}
            action={
              <Button type="button" variant="secondary" onClick={loadCommittees}>
                Refresh
              </Button>
            }
          />
        ) : committeesLoading ? (
          <PageState
            state="loading"
            align="left"
            compact
            title="Loading committees"
            description="Glyder is preparing committee membership and governance groups."
          />
        ) : committees.length === 0 ? (
          <PageState
            state="empty"
            align="left"
            compact
            title="No committees found"
            description="Create a committee to manage shared review groups and board-pack access."
          />
        ) : (
          <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
            {committees.map((committee) => {
              const memberIds = committeeMembers[committee.id] || [];
              const members = memberIds.map((id) => userMap.get(id) || { id });
              const availableUsers = users.filter((item) => !memberIds.includes(item.id));
              return (
                <div key={committee.id} style={committeeCardStyle}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{committee.name}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        {committee.description || "No description provided."}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Created {formatDateTime(committee.created_at)}
                    </div>
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Members</div>
                    {members.length === 0 ? (
                      <div style={{ fontSize: 13, color: "#9ca3af" }}>No members assigned.</div>
                    ) : (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {members.map((member) => (
                          <div key={member.id} style={memberChipStyle}>
                            <span>{member.username || member.email || `User #${member.id}`}</span>
                            <Button
                              type="button"
                              style={chipRemoveStyle}
                              onClick={() => requestMemberRemove(committee.id, member.id)}
                              title="Remove member"
                              variant="ghost"
                            >
                              x
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {confirmCommitteeRemove && confirmCommitteeRemove.committeeId === committee.id && (
                      <DestructiveConfirm
                        actionId="COMMITTEE_MEMBER_REMOVE"
                        open
                        resourceDisplayName={confirmCommitteeRemove.label}
                        variantOverride="inline"
                        onCancel={() => setConfirmCommitteeRemove(null)}
                        onConfirm={async () => {
                          const pending = confirmCommitteeRemove;
                          setConfirmCommitteeRemove(null);
                          if (!pending) return undefined;
                          await handleMemberRemove(pending.committeeId, pending.userId);
                          return async () => {
                            try {
                              await addCommitteeMembers(pending.committeeId, [Number(pending.userId)]);
                              setCommitteeMembers((prev) => {
                                const existing = new Set(prev[pending.committeeId] || []);
                                existing.add(Number(pending.userId));
                                return { ...prev, [pending.committeeId]: Array.from(existing) };
                              });
                            } catch (err) {
                              setCommitteesError(err?.detail || err?.message || "Unable to re-add member.");
                            }
                          };
                        }}
                      />
                    )}
                  </div>

                  <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
                    <Select
                      value={committeeSelections[committee.id] || ""}
                      onChange={(e) =>
                        setCommitteeSelections((prev) => ({
                          ...prev,
                          [committee.id]: e.target.value,
                        }))
                      }
                      options={[
                        { value: "", label: "Select user" },
                        ...availableUsers.map((item) => ({
                          value: item.id,
                          label: item.username || item.email,
                        })),
                      ]}
                      style={{ minWidth: 200 }}
                    />
                    <Button
                      variant="secondary"
                      onClick={() => handleMemberAdd(committee.id)}
                      disabled={!committeeSelections[committee.id]}
                    >
                      Add Member
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderAuditLogsTab = () => {
    const auditColumns = [
      {
        id: "timestamp",
        header: "Timestamp",
        accessor: (row) => formatDateTime(row.timestamp),
      },
      {
        id: "actor",
        header: "Actor",
        accessor: (row) => {
          const actor = userMap.get(row.actor_id);
          return actor?.username || actor?.email || `User #${row.actor_id}`;
        },
      },
      { id: "action", header: "Action", accessor: "action" },
      {
        id: "target",
        header: "Target",
        accessor: (row) => `${row.target_type} ${row.target_id}`,
      },
      { id: "from", header: "From", accessor: (row) => row.from_value || "-" },
      { id: "to", header: "To", accessor: (row) => row.to_value || "-" },
    ];

    return (
      <div style={{ display: "grid", gap: 16 }}>
        <div style={cardStyle}>
          <h2 style={{ marginTop: 0 }}>Audit Logs</h2>
          <p style={{ marginBottom: 0, color: "#475467" }}>
            Track administrative changes across users and committees.
          </p>
        </div>

        <div style={cardStyle}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            <Select
              label="Actor"
              value={auditFilters.actorId}
              onChange={(e) => setAuditFilters((prev) => ({ ...prev, actorId: e.target.value }))}
              options={[
                { value: "", label: "All" },
                ...users.map((item) => ({ value: item.id, label: item.username || item.email })),
              ]}
            />
            <Input
              label="Action"
              value={auditFilters.action}
              onChange={(e) => setAuditFilters((prev) => ({ ...prev, action: e.target.value }))}
              placeholder="user.role.update"
            />
            <Input
              label="Target Type"
              value={auditFilters.targetType}
              onChange={(e) => setAuditFilters((prev) => ({ ...prev, targetType: e.target.value }))}
              placeholder="user or committee"
            />
          </div>
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <Button variant="primary" onClick={loadAuditLogs} disabled={auditLoading}>
              {auditLoading ? "Loading..." : "Apply Filters"}
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                setAuditFilters({
                  actorId: "",
                  action: "",
                  targetType: "",
                })
              }
            >
              Clear
            </Button>
          </div>
          {auditSummary.total > 0 && (
            <div style={{ marginTop: 12, color: "#475467" }}>
              Showing {auditLogs.length} of {auditSummary.total} audit log entries.
            </div>
          )}
          {auditError && auditLogs.length > 0 && (
            <div style={{ marginTop: 12, color: "#b91c1c" }}>{auditError}</div>
          )}
        </div>

        <div style={cardStyle}>
          {auditError && auditLogs.length === 0 ? (
            <PageState
              state="error"
              align="left"
              compact
              title={auditSupported ? "Audit logs are unavailable" : "Audit logs are not enabled"}
              description={auditError}
              action={
                auditSupported ? (
                  <Button type="button" variant="secondary" onClick={loadAuditLogs}>
                    Refresh
                  </Button>
                ) : null
              }
            />
          ) : auditLoading ? (
            <PageState
              state="loading"
              align="left"
              compact
              title="Loading audit logs"
              description="Glyder is preparing the latest administrative activity."
            />
          ) : auditSupported && auditLogs.length === 0 ? (
            <PageState
              state="empty"
              align="left"
              compact
              title="No audit log entries found"
              description="Administrative changes will appear here once activity is recorded."
            />
          ) : auditSupported && auditLogs.length > 0 ? (
            <Table
              columns={auditColumns}
              data={auditLogs}
              getRowKey={(row) => row.id || `${row.actor_id}-${row.timestamp}`}
              emptyState="No audit log entries found."
            />
          ) : null}
        </div>
      </div>
    );
  };

  const renderSettingsTab = () => (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Settings</h2>
        <p style={{ marginBottom: 0, color: "#475467" }}>
          Administrative policy controls are not enabled in this environment. Review the current defaults below while
          tenant-level enforcement remains unavailable.
        </p>
      </div>

      <div style={cardStyle}>
        <div style={{ display: "grid", gap: 16, maxWidth: 420 }}>
          <Select
            label="Password Policy"
            value={settingsState.passwordPolicy}
            onChange={(e) => setSettingsState((prev) => ({ ...prev, passwordPolicy: e.target.value }))}
            options={[
              { value: "standard", label: "Standard (8+ characters)" },
              { value: "strict", label: "Strict (12+ characters, symbols)" },
              { value: "custom", label: "Custom" },
            ]}
          />
          <Input
            label="Session Timeout (minutes)"
            type="number"
            min="5"
            value={settingsState.sessionTimeout}
            onChange={(e) =>
              setSettingsState((prev) => ({
                ...prev,
                sessionTimeout: Number(e.target.value),
              }))
            }
          />
          <label style={toggleLabelStyle}>
            <input
              type="checkbox"
              checked={settingsState.mfaRequired}
              onChange={(e) =>
                setSettingsState((prev) => ({ ...prev, mfaRequired: e.target.checked }))
              }
            />
            Require MFA for all users
          </label>
          <Button variant="secondary" disabled>
            Configuration controls unavailable
          </Button>
          <div style={{ fontSize: 12, color: "#475467" }}>
            Settings can be reviewed here, but changes cannot be saved in this environment.
          </div>
        </div>
      </div>
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Reset Password Confirmation</h3>
        <p style={{ marginBottom: 0, color: "#475467" }}>
          Confirm one-time reset tokens. Token values are cleared from UI state after submission.
        </p>
        <form onSubmit={handleResetConfirmSubmit} style={{ display: "grid", gap: 12, maxWidth: 520, marginTop: 12 }}>
          <Input
            label="Reset Token"
            value={resetConfirmForm.token}
            onChange={(e) => setResetConfirmForm((prev) => ({ ...prev, token: e.target.value }))}
            autoComplete="off"
          />
          <Input
            label="New Password"
            type="password"
            value={resetConfirmForm.newPassword}
            onChange={(e) => setResetConfirmForm((prev) => ({ ...prev, newPassword: e.target.value }))}
            autoComplete="new-password"
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Button type="submit" variant="secondary" disabled={resetConfirmSaving}>
              {resetConfirmSaving ? "Confirming..." : "Confirm Reset"}
            </Button>
            {resetConfirmMessage && <span style={{ color: "#166534" }}>{resetConfirmMessage}</span>}
            {resetConfirmError && <span style={{ color: "#b91c1c" }}>{resetConfirmError}</span>}
          </div>
        </form>
      </div>
    </div>
  );

  const renderAccessEditor = () => (
    <DrawerA11yWrapper
      open={accessEditorOpen}
      onClose={closeAccessEditor}
      labelledBy={accessDrawerTitleId}
      initialFocusRef={accessDrawerCloseRef}
      backdropStyle={drawerOverlayStyle}
      containerStyle={drawerContainerStyle}
    >
      <div style={drawerLayoutStyle}>
        <div style={drawerHeaderStyle}>
          <div>
            <div id={accessDrawerTitleId} style={{ fontSize: 18, fontWeight: 600 }}>
              Edit Access
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
              {accessEditorDetail?.username ||
                accessEditorDetail?.email ||
                accessEditorTargetUser?.username ||
                accessEditorTargetUser?.email ||
                "Loading user"}
            </div>
          </div>
          <Button
            onClick={closeAccessEditor}
            variant="ghost"
            style={closeButtonStyle}
            aria-label="Close access editor"
            ref={accessDrawerCloseRef}
          >
            ×
          </Button>
        </div>

        <div style={drawerBodyStyle}>
          {accessEditorLoading ? (
            <PageState
              state="loading"
              align="left"
              compact
              title="Loading user access"
              description="Glyder is preparing the current access profile and module-level access."
            />
          ) : accessEditorError && !accessEditorDetail ? (
            <PageState
              state="error"
              align="left"
              compact
              title="Access details are unavailable"
              description={accessEditorError}
              action={
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => accessEditorTargetUser?.id && openAccessEditor(accessEditorTargetUser)}
                  disabled={!accessEditorTargetUser?.id}
                >
                  Retry
                </Button>
              }
            />
          ) : !accessEditorDetail ? (
            <PageState
              state="empty"
              align="left"
              compact
              title="No access details available"
              description="This user does not currently have editable access data loaded."
              action={
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => accessEditorTargetUser?.id && openAccessEditor(accessEditorTargetUser)}
                  disabled={!accessEditorTargetUser?.id}
                >
                  Reload
                </Button>
              }
            />
          ) : accessEditorDetail ? (
            <div style={{ display: "grid", gap: 16 }}>
              <div style={editorSectionStyle}>
                <h3 style={{ margin: 0 }}>User Context</h3>
                <div style={{ display: "grid", gap: 8, fontSize: 13 }}>
                  <div>Email: {accessEditorDetail.email || "-"}</div>
                  <div>Legacy role: {accessEditorDetail.legacy_role || accessEditorDetail.role || "-"}</div>
                  <div>Status: {accessEditorDetail.status || "-"}</div>
                  {accessIsCurrentUser && (
                    <div style={{ color: "#6b7280" }}>
                      You are editing your own access. Final admin protections are still enforced by the backend.
                    </div>
                  )}
                </div>
              </div>

              <div style={editorSectionStyle}>
                <h3 style={{ margin: 0 }}>Generic Profile</h3>
                <p style={sectionHelperTextStyle}>
                  Profiles apply backend-defined defaults. Individual module settings override those defaults. Legacy role applies only to specialist business rules.
                </p>
                <Select
                  label="Access Profile"
                  value={accessFormProfile}
                  onChange={(e) => handleAccessProfileSelect(e.target.value)}
                  options={accessProfileDefinitions.map((profile) => ({
                    value: profile.key,
                    label: profile.label,
                  }))}
                  disabled={accessEditorSaving || accessConfigLoading}
                />
                <div style={{ fontSize: 13, color: accessHasOverrides ? "#92400e" : "#6b7280" }}>
                  {accessHasOverrides
                    ? `${getProfileLabel(accessProfileDefinitions, accessFormProfile)} profile with module overrides.`
                    : "Profile defaults are applied. Individual settings override them; legacy Role never grants denied module access."}
                </div>
              </div>

              <div style={editorSectionStyle}>
                <h3 style={{ margin: 0 }}>Module Access</h3>
                <p style={sectionHelperTextStyle}>
                  Editing individual modules creates custom access overrides. Backend validation still decides what is accepted.
                </p>
                <div style={{ display: "grid", gap: 10 }}>
                  {moduleDefinitions.map((moduleDef) => (
                    <div key={moduleDef.key} style={moduleRowStyle}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{moduleDef.label}</div>
                        {moduleDef.key === "tasks" && <div style={inlineHelperStyle}>Task Centre also requires Actions access; Actions permissions govern its shared records.</div>}
                        {moduleDef.key === "overview" && <div style={inlineHelperStyle}>Opens the overview; each data source still requires its own module access.</div>}
                        <div style={inlineHelperStyle}>
                          Default: {selectedProfileDefaults[moduleDef.key] || "NONE"}
                        </div>
                      </div>
                      <Select
                        ariaLabel={`${moduleDef.label} access`}
                        value={accessFormModules[moduleDef.key] || "NONE"}
                        onChange={(e) => handleAccessModuleChange(moduleDef.key, e.target.value)}
                        options={accessLevelDefinitions.map((level) => ({
                          value: level.key,
                          label: level.label,
                        }))}
                        disabled={accessEditorSaving}
                        style={{ minWidth: 160 }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {(accessEditorError || accessEditorMessage) && (
                <div style={{ color: accessEditorError ? "#b91c1c" : "#166534" }}>
                  {accessEditorError || accessEditorMessage}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <Button variant="secondary" onClick={closeAccessEditor} disabled={accessEditorSaving}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleAccessSave} disabled={accessEditorSaving}>
                  {accessEditorSaving ? "Saving..." : "Save Access"}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </DrawerA11yWrapper>
  );

  if (!accessReady) {
    return (
      <PageState
        state="loading"
        align="left"
        title="Loading Admin User Portal"
        description="Glyder is verifying your current Admin User Portal access."
      />
    );
  }

  if (!canManagePortal) {
    return (
      <PageState
        state="empty"
        align="left"
        title="Admin User Portal access required"
        description="You need Manage access to Admin User Portal to manage administrative settings in this workspace."
      />
    );
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Admin Portal</h2>
        <p style={{ marginBottom: 0, color: "#475467" }}>
          Manage users, committees, and governance settings.
        </p>
      </div>

      <div style={tabStripStyle}>
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <Button
              key={tab.key}
              variant="secondary"
              type="button"
              onClick={() => handleTabSelect(tab.key)}
              style={{
                ...tabButtonStyle,
                ...(isActive ? tabButtonActiveStyle : null),
              }}
            >
              {tab.label}
            </Button>
          );
        })}
      </div>

      {activeTab === "users" && renderUsersTab()}
      {activeTab === "committees" && renderCommitteesTab()}
      {activeTab === "audit-logs" && renderAuditLogsTab()}
      {activeTab === "settings" && renderSettingsTab()}

      {renderAccessEditor()}

      <DestructiveConfirm
        actionId="USER_PASSWORD_RESET"
        open={Boolean(confirmResetUserId)}
        resourceDisplayName={
          confirmResetUserId ? userMap.get(confirmResetUserId)?.username || userMap.get(confirmResetUserId)?.email : undefined
        }
        onCancel={() => setConfirmResetUserId(null)}
        onConfirm={() => {
          const userId = confirmResetUserId;
          setConfirmResetUserId(null);
          if (userId) {
            handlePasswordReset(userId);
          }
        }}
      />
      <ConfirmDialog
        open={Boolean(confirmRoleChange)}
        title="Confirm Role Change"
        message={
          confirmRoleChange ? (
            <div style={{ display: "grid", gap: 6 }}>
              <p style={{ margin: 0 }}>
                You are changing{" "}
                <strong>
                  {userMap.get(confirmRoleChange.userId)?.username ||
                    userMap.get(confirmRoleChange.userId)?.email ||
                    "this user"}
                </strong>{" "}
                from <strong>{confirmRoleChange.previousRole}</strong> to{" "}
                <strong>{confirmRoleChange.nextRole}</strong>.
              </p>
              <p style={{ margin: 0 }}>
                Role changes affect specialist business rules. They do not change the Access Profile or module settings; use Edit Access to change those.
              </p>
            </div>
          ) : undefined
        }
        confirmLabel="Change Role"
        cancelLabel="Cancel"
        confirmVariant="destructive"
        onCancel={() => setConfirmRoleChange(null)}
        onConfirm={() => {
          const pending = confirmRoleChange;
          setConfirmRoleChange(null);
          if (pending) {
            handleRoleChange(pending.userId, pending.nextRole);
          }
        }}
      />
      <DestructiveConfirm
        actionId="USER_STATUS_UPDATE"
        open={Boolean(confirmStatusChange)}
        resourceDisplayName={
          confirmStatusChange ? userMap.get(confirmStatusChange.userId)?.username || userMap.get(confirmStatusChange.userId)?.email : undefined
        }
        context={
          confirmStatusChange
            ? {
                scopeLabel: `Set status to ${confirmStatusChange.nextStatus}`,
                irreversibleNote:
                  confirmStatusChange.nextStatus === "SUSPENDED"
                    ? "Suspended users lose access until another admin reactivates them. Final admin protections still apply."
                    : "Reactivated users regain access on their next authenticated request.",
              }
            : undefined
        }
        onCancel={() => setConfirmStatusChange(null)}
        onConfirm={() => {
          const pending = confirmStatusChange;
          setConfirmStatusChange(null);
          if (pending) {
            handleStatusUpdate(pending.userId, pending.nextStatus);
          }
        }}
      />
    </div>
  );
}

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 16,
  boxShadow: "0 6px 16px rgba(15, 23, 42, 0.04)",
};

const tabStripStyle = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
};

const tabButtonStyle = {
  borderRadius: 999,
  padding: "8px 16px",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "#d1d5db",
  background: "#ffffff",
  color: "#111827",
  boxShadow: "none",
};

const tabButtonActiveStyle = {
  background: "#111827",
  borderColor: "#111827",
  color: "#ffffff",
};

const committeeCardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 12,
  background: "#f9fafb",
};

const memberChipStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 10px",
  borderRadius: 999,
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  fontSize: 12,
};

const chipRemoveStyle = {
  border: "none",
  background: "transparent",
  color: "#ef4444",
  cursor: "pointer",
  fontSize: 12,
  lineHeight: 1,
};

const toggleLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  color: "#111827",
};

const inlineHelperStyle = {
  fontSize: 12,
  color: "#6b7280",
};

const drawerOverlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "stretch",
  zIndex: 3000,
};

const drawerContainerStyle = {
  width: "640px",
  maxWidth: "94vw",
  height: "100%",
  background: "#ffffff",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 0 20px rgba(0,0,0,0.15)",
};

const drawerLayoutStyle = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
};

const drawerHeaderStyle = {
  padding: "16px 20px",
  borderBottom: "1px solid #e5e7eb",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#fff",
  position: "sticky",
  top: 0,
  zIndex: 10,
};

const drawerBodyStyle = {
  padding: "20px",
  overflowY: "auto",
  flex: 1,
};

const closeButtonStyle = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontSize: "20px",
  color: "#6b7280",
  padding: "4px 8px",
};

const editorSectionStyle = {
  display: "grid",
  gap: 10,
  padding: 16,
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  background: "#f8fafc",
};

const sectionHelperTextStyle = {
  margin: 0,
  fontSize: 13,
  color: "#475467",
};

const moduleRowStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 180px",
  gap: 12,
  alignItems: "center",
  paddingBottom: 10,
  borderBottom: "1px solid #e5e7eb",
};
